import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Breadcrumbs from '../components/Breadcrumbs';
import { seoData } from '../data/seoData';
import ADACPartner from '../components/ADACPartner';

const PreisePage = () => {
  const seo = seoData['/preise/'];
  
  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href="https://schluesseldienst.services/preise/" />
      </Helmet>
      
      <div className="container mx-auto px-4 py-12">
        <Breadcrumbs items={[{ label: 'Preise' }]} />
        
        <h1 className="text-4xl sm:text-5xl font-bold mb-8">Preise für Türöffnung in Dortmund</h1>
        
        <div className="max-w-4xl">
          <div className="bg-orange-50 border-l-4 border-orange-600 p-6 mb-8">
            <p className="text-lg">
              <strong>Transparenz ist uns wichtig:</strong> Alle Preise verstehen sich inklusive Anfahrt, Arbeitszeit und Mehrwertsteuer. 
              Keine versteckten Kosten!
            </p>
          </div>

          {/* Preistabelle */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-12">
            <table className="w-full">
              <thead className="bg-orange-600 text-white">
                <tr>
                  <th className="p-4 text-left">Zeitfenster</th>
                  <th className="p-4 text-left">Leistung</th>
                  <th className="p-4 text-right">Preis (inkl. MwSt.)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-4 font-semibold">Mo–Fr 08–20 Uhr</td>
                  <td className="p-4">Tür zugefallen (einfache Öffnung)</td>
                  <td className="p-4 text-right text-2xl font-bold text-orange-600">119 €</td>
                </tr>
                <tr className="border-b bg-orange-50">
                  <td className="p-4 font-semibold">Sa–So</td>
                  <td className="p-4">Tür zugefallen (einfache Öffnung)</td>
                  <td className="p-4 text-right text-2xl font-bold text-orange-600">199 €</td>
                </tr>
              </tbody>
            </table>
            <div className="p-6 bg-gray-50">
              <h3 className="font-bold mb-3">Im Preis enthalten:</h3>
              <ul className="space-y-2 text-sm">
                <li>✓ Anfahrt in ganz Dortmund</li>
                <li>✓ Arbeitszeit und Öffnung</li>
                <li>✓ 19% Mehrwertsteuer</li>
                <li>✓ Fachgerechte Beratung vor Ort</li>
              </ul>
            </div>
          </div>

          {/* Zahlungsarten */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-4">Zahlungsarten</h2>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="mb-4">Für Ihre Bequemlichkeit akzeptieren wir folgende Zahlungsarten:</p>
              <div className="flex flex-wrap gap-4">
                <span className="bg-orange-100 text-orange-800 px-4 py-2 rounded-full font-semibold">Barzahlung</span>
                <span className="bg-orange-100 text-orange-800 px-4 py-2 rounded-full font-semibold">EC-Karte</span>
                <span className="bg-orange-100 text-orange-800 px-4 py-2 rounded-full font-semibold">Kreditkarte</span>
              </div>
            </div>
          </div>

          {/* Beispielrechnungen */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">Beispielrechnungen</h2>
            
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-xl font-bold mb-3">Beispiel 1: Tür zugefallen, Dienstag 14 Uhr</h3>
                <div className="space-y-2 text-gray-700">
                  <div className="flex justify-between">
                    <span>Türöffnung Mo-Fr 08-20 Uhr</span>
                    <span className="font-semibold">100,00 €</span>
                  </div>
                  <div className="flex justify-between">
                    <span>19% MwSt.</span>
                    <span className="font-semibold">19,00 €</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between text-xl font-bold text-orange-600">
                    <span>Gesamt</span>
                    <span>119,00 €</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-xl font-bold mb-3">Beispiel 2: Tür zugefallen, Samstag 16 Uhr</h3>
                <div className="space-y-2 text-gray-700">
                  <div className="flex justify-between">
                    <span>Türöffnung Sa-So</span>
                    <span className="font-semibold">167,23 €</span>
                  </div>
                  <div className="flex justify-between">
                    <span>19% MwSt.</span>
                    <span className="font-semibold">31,77 €</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between text-xl font-bold text-orange-600">
                    <span>Gesamt</span>
                    <span>199,00 €</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-orange-600 to-amber-500 text-white rounded-2xl p-8 text-center mb-8">
            <h2 className="text-2xl font-bold mb-4">Jetzt Hilfe anfordern!</h2>
            <p className="mb-6">Rufen Sie uns für eine schnelle Türöffnung an:</p>
            <a
              href="tel:+4923139085116"
              className="inline-block bg-white text-orange-600 px-8 py-4 rounded-full text-xl font-bold hover:bg-gray-100 transition-all shadow-lg"
              data-testid="preise-cta-call"
            >
              0231 39085116
            </a>
          </div>
          
          {/* ADAC Partner Info */}
          <ADACPartner variant="full" />
        </div>
      </div>
    </>
  );
};

export default PreisePage;
